/**
 * Environment configuration for the admin app.
 *
 * Mirrors packages/frontend/src/config.ts's hostname-resolution pattern: the
 * deployed hostname decides API config, not which .env.[mode] file built the
 * bundle, so one build artifact is correct on both staging and production —
 * see deploy-admin-production.yml, which promotes the exact bytes
 * deploy-admin-staging.yml built rather than rebuilding.
 *
 * The admin hostnames themselves (admin.spark.charisol.io /
 * staging.admin.spark.charisol.io) sit beside the user app's hostnames in
 * the charisol.io Cloudflare zone, and are documented in
 * docs/runbooks/deployment.md.
 */

const PROD_HOSTNAME_SUFFIXES = ["admin.spark.charisol.io"];
const STAGING_HOSTNAME_SUFFIXES = ["staging.admin.spark.charisol.io"];

const PROD = {
  apiUrl: "https://api.spark.aws.charisol.io",
  /** The user app this console's "Back to App" link and interviews-tracker
   * link point at — packages/frontend's own production hostname. */
  appUrl: "https://spark.charisol.io",
} as const;

const STAGING = {
  apiUrl: "https://staging-api.spark.aws.charisol.io",
  appUrl: "https://staging.spark.charisol.io",
} as const;

function resolveByHostname(): typeof PROD | typeof STAGING | null {
  if (typeof window === "undefined") return null;
  const hostname = window.location.hostname;
  if (PROD_HOSTNAME_SUFFIXES.includes(hostname)) return PROD;
  if (STAGING_HOSTNAME_SUFFIXES.includes(hostname)) return STAGING;
  return null; // localhost, PR previews, anything else
}

const resolved = resolveByHostname();
const hostname = typeof window === "undefined" ? "" : window.location.hostname;
const isLocalHostname =
  hostname === "" ||
  hostname === "localhost" ||
  hostname === "127.0.0.1" ||
  hostname === "::1";

const stage =
  resolved === PROD
    ? "prod"
    : resolved === STAGING
      ? "staging"
      : import.meta.env.VITE_STAGE || (isLocalHostname ? "dev" : "staging");

/**
 * Label for the top-nav environment marker, or null to show nothing.
 *
 * Deliberately resolved from `resolved`/`hostname` (pure runtime hostname
 * checks), never from `stage` -- `stage`'s own fallback branch above bakes
 * in VITE_STAGE at build time for the no-match case, and the same build is
 * promoted from staging to prod (see this file's top comment), so a marker
 * that told them apart via a build-time value would show "staging" on the
 * promoted production bundle too. Checking the hostname directly is what
 * keeps that one build correct on both domains.
 */
const environmentBadge: "Staging" | "Preview" | "Local" | null =
  resolved === STAGING
    ? "Staging"
    : resolved === PROD
      ? null
      : isLocalHostname
        ? "Local"
        : hostname.endsWith(".pages.dev")
          ? "Preview"
          : null;

/**
 * Resolve a required env variable for the non-hostname-matched (dev/preview)
 * path. Throws when unset, same validation rule as the frontend app's
 * config.ts: a missing value on this path is always a misconfiguration.
 */
function requireEnv(key: keyof ImportMetaEnv): string {
  const value = import.meta.env[key];
  if (value) return value;
  throw new Error(
    `[config] Missing required environment variable "${key}" for stage "${stage}". ` +
      `For local development, set VITE_API_URL in packages/admin/.env.local.`
  );
}

export const config = {
  apiUrl: resolved?.apiUrl ?? requireEnv("VITE_API_URL"),
  // Not required on the dev/preview path — used only by two non-blocking
  // links (AdminLayout's "Back to App", AdminInterviewsPage's link to the
  // public tracker), so a missing value there degrades those links rather
  // than crashing the whole app at startup.
  appUrl: resolved?.appUrl ?? import.meta.env.VITE_APP_URL ?? "",
  stage,
  isProduction: stage === "prod",
  isStaging: stage === "staging",
  isDev: stage === "dev" || import.meta.env.DEV,
  /** Top-nav environment marker label; null hides it (production). */
  environmentBadge,
} as const;
